import { patchScopedSettingValuesMutationAtom } from '@rematter/modules/settings/_graphql/mutationPatchScopedSettingValues';
import { toastQueue } from '@rematter/ui-web';
import type { Getter, Setter } from 'jotai';
import { atom } from 'jotai';

import { itemGroupUpdateAtom } from '~/modules/settings/_graphql/mutationItemGroupUpdate';
import { itemUpdateAtom } from '~/modules/settings/_graphql/mutationItemUpdate';
import type { SettingsGroupRecord } from '~/modules/settings/types/settings.types';
import { $inventoryItems } from '~/modules/trade/_state/loadForm.state';
import { $user } from '~/shared/providers/auth/identity.localState';
import { $touchModeEnabled } from '~/shared/state/preferences.state';
import {
  $allSettingsGroups,
  $settingsReadPermission,
} from '~/shared/state/settings.state';

const SETTING_IDS = {
  touchMode: 'scaleTicket.touchModeEnabled',
  showExtras: 'scaleTicket.showTicketExtras',
  disabledItemTypes: 'scaleTicket.disabledItemTypes',
  quickAddItemIds: 'scaleTicket.quickAddItemIds',
  quickAddGroupIds: 'scaleTicket.quickAddGroupIds',
  touchModalGroupOrderIds: 'scaleTicket.touchModalGroupOrderIds',
  touchModalItemOrderByGroup: 'scaleTicket.touchModalItemOrderByGroup',
} as const;

const findSettingUserValue = (
  groups: SettingsGroupRecord[],
  settingId: string,
): unknown | undefined => {
  for (const group of groups) {
    const setting = group.settings.find((s) => s.id === settingId);
    if (setting) {
      const userVal = setting.values.find((v) => v.scope === 'User');
      if (userVal) return userVal.value;
      return setting.defaultValue;
    }
  }
  return undefined;
};

const findSettingHasUserValue = (
  groups: SettingsGroupRecord[],
  settingId: string,
): boolean => {
  for (const group of groups) {
    const setting = group.settings.find((s) => s.id === settingId);
    if (setting) {
      return setting.values.some((v) => v.scope === 'User');
    }
  }
  return false;
};

const UNSET = Symbol('unset');

// 🟠🟠🟠 ACTION -> PATCH scale ticket setting
const $patchScaleTicketSetting = patchScopedSettingValuesMutationAtom({
  onSuccess: (get, set) => {
    get($allSettingsGroups)
      .refetch()
      .then(() => {
        set($touchModeOverride, UNSET);
        set($showExtrasOverride, UNSET);
      });
  },
});

const patchSetting = (
  get: Getter,
  set: Setter,
  settingId: string,
  value: unknown,
) => {
  const user = get($user);
  if (!user) return;
  set($patchScaleTicketSetting, {
    scope: 'User' as const,
    objectId: user.id,
    values: { [settingId]: value },
  });
};

// Touch mode migration flag — ensures one-time migration runs only once per session
const $touchModeMigrationDone = atom(false);

export const DEFAULT_ITEM_TYPE_ORDER = [
  'Raw',
  'Tagged',
  'CatalyticConverter',
  'Mixed',
  'Vehicle',
  'Freight',
  'Deduction',
] as const;

export type ItemTypeConfig = {
  disabled: Record<string, boolean>;
  order: string[];
};

// 🔘🔘🔘 PLAIN ATOMS — optimistic local overrides (applied immediately, cleared on refetch)
const $touchModeOverride = atom<boolean | typeof UNSET>(UNSET);
const $showExtrasOverride = atom<boolean | typeof UNSET>(UNSET);
const $disabledItemTypesOverride = atom<ItemTypeConfig | typeof UNSET>(UNSET);
const $quickAddItemIdsOverride = atom<string[] | typeof UNSET>(UNSET);
const $quickAddGroupIdsOverride = atom<string[] | typeof UNSET>(UNSET);

const $itemTypeEditModeInternal = atom(false);
export const $itemTypeEditMode = atom(
  (get) => get($itemTypeEditModeInternal),
  (_get, set, newValue: boolean) => {
    if (!newValue) {
      set($disabledItemTypesOverride, UNSET);
    }
    set($itemTypeEditModeInternal, newValue);
  },
);

export const $saveItemTypeConfig = atom(null, (get, set) => {
  const override = get($disabledItemTypesOverride);
  if (override !== UNSET) {
    patchSetting(get, set, SETTING_IDS.disabledItemTypes, override);
  }
  set($itemTypeEditModeInternal, false);
});

const $quickAddItemsEditModeInternal = atom(false);
export const $quickAddItemsEditMode = atom(
  (get) => get($quickAddItemsEditModeInternal),
  (_get, set, newValue: boolean) => {
    if (!newValue) {
      set($quickAddItemIdsOverride, UNSET);
    }
    set($quickAddItemsEditModeInternal, newValue);
  },
);

export const $saveQuickAddItems = atom(null, (get, set) => {
  const override = get($quickAddItemIdsOverride);
  if (override !== UNSET) {
    patchSetting(get, set, SETTING_IDS.quickAddItemIds, override);
  }
  set($quickAddItemsEditModeInternal, false);
});

// 🟢🟢🟢 COMPUTED DATA -> Scale ticket touch mode
export const $scaleTicketTouchMode = atom(
  (get) => {
    const override = get($touchModeOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    const localStorageValue = get($touchModeEnabled);

    if (!data?.settingsGroups) {
      return localStorageValue === true;
    }

    const hasUserValue = findSettingHasUserValue(
      data.settingsGroups,
      SETTING_IDS.touchMode,
    );

    if (!hasUserValue) {
      const migrationDone = get($touchModeMigrationDone);
      if (!migrationDone && localStorageValue === true) {
        return true;
      }
    }

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.touchMode,
    );
    if (typeof value === 'string') return value === 'true';
    if (typeof value === 'boolean') return value;
    return false;
  },
  (get, set, newValue: boolean) => {
    set($touchModeOverride, newValue);
    patchSetting(get, set, SETTING_IDS.touchMode, newValue);
  },
);

// One-time migration: promote localStorage touch mode to scoped settings
export const $touchModeMigrationEffect = atom((get) => {
  const migrationDone = get($touchModeMigrationDone);
  if (migrationDone) return;

  const hasPermission = get($settingsReadPermission).data;
  if (!hasPermission) return;

  const { data } = get($allSettingsGroups);
  if (!data?.settingsGroups) return;

  const hasUserValue = findSettingHasUserValue(
    data.settingsGroups,
    SETTING_IDS.touchMode,
  );
  if (hasUserValue) return;

  const localStorageValue = get($touchModeEnabled);
  if (localStorageValue !== true) return;

  return { shouldMigrate: true } as const;
});

export const $runTouchModeMigration = atom(null, (get, set) => {
  const migration = get($touchModeMigrationEffect);
  if (!migration?.shouldMigrate) return;

  const user = get($user);
  if (!user) return;

  set($touchModeMigrationDone, true);
  patchSetting(get, set, SETTING_IDS.touchMode, 'true');
});

// 🟢🟢🟢 COMPUTED DATA -> Show ticket extras
export const $scaleTicketShowExtras = atom(
  (get) => {
    const override = get($showExtrasOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return true;

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.showExtras,
    );
    if (typeof value === 'string') return value === 'true';
    if (typeof value === 'boolean') return value;
    return true;
  },
  (get, set, newValue: boolean) => {
    set($showExtrasOverride, newValue);
    patchSetting(get, set, SETTING_IDS.showExtras, newValue);
  },
);

const parseItemTypeConfig = (value: unknown): ItemTypeConfig => {
  const defaultConfig: ItemTypeConfig = {
    disabled: {},
    order: [...DEFAULT_ITEM_TYPE_ORDER],
  };
  if (!value || typeof value !== 'object') return defaultConfig;
  const obj = value as Record<string, unknown>;
  if ('order' in obj && Array.isArray(obj.order)) {
    return {
      disabled:
        obj.disabled && typeof obj.disabled === 'object'
          ? (obj.disabled as Record<string, boolean>)
          : {},
      order: obj.order as string[],
    };
  }
  if (!('order' in obj)) {
    return {
      disabled: obj as Record<string, boolean>,
      order: [...DEFAULT_ITEM_TYPE_ORDER],
    };
  }
  return defaultConfig;
};

// 🟢🟢🟢 COMPUTED DATA -> Item type config (disabled + order)
export const $scaleTicketItemTypeConfig = atom(
  (get) => {
    const override = get($disabledItemTypesOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) {
      return {
        disabled: {},
        order: [...DEFAULT_ITEM_TYPE_ORDER],
      } as ItemTypeConfig;
    }

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.disabledItemTypes,
    );
    return parseItemTypeConfig(value);
  },
  (_get, set, newValue: ItemTypeConfig) => {
    set($disabledItemTypesOverride, newValue);
  },
);

export const $scaleTicketDisabledItemTypes = atom(
  (get) => get($scaleTicketItemTypeConfig).disabled,
  (get, set, newDisabled: Record<string, boolean>) => {
    const config = get($scaleTicketItemTypeConfig);
    set($scaleTicketItemTypeConfig, { ...config, disabled: newDisabled });
  },
);

// 🟢🟢🟢 COMPUTED DATA -> Quick-add item IDs
export const $scaleTicketQuickAddItemIds = atom(
  (get) => {
    const override = get($quickAddItemIdsOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return [] as string[];

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.quickAddItemIds,
    );
    if (Array.isArray(value)) return value as string[];
    return [] as string[];
  },
  (_get, set, newValue: string[]) => {
    set($quickAddItemIdsOverride, newValue);
  },
);

// 🟢🟢🟢 COMPUTED DATA -> Quick-add group IDs
export const $scaleTicketQuickAddGroupIds = atom(
  (get) => {
    const override = get($quickAddGroupIdsOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return [] as string[];

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.quickAddGroupIds,
    );
    if (Array.isArray(value)) return value as string[];
    return [] as string[];
  },
  (_get, set, newValue: string[]) => {
    set($quickAddGroupIdsOverride, newValue);
  },
);

// ─────────────────────────────────────────────────
// Touch Modal Edit Mode
// ─────────────────────────────────────────────────

export type TouchModalColorChange = {
  type: 'group' | 'item';
  colorHue: number | null;
};

const $touchModalEditModeInternal = atom(false);

// 🔘🔘🔘 PLAIN ATOMS — touch modal local overrides
const $touchModalGroupIdsOverride = atom<string[] | typeof UNSET>(UNSET);
const $touchModalGroupOrderIdsOverride = atom<string[] | typeof UNSET>(UNSET);
const $touchModalItemFavoritesByGroupOverride = atom<
  Record<string, string[]> | typeof UNSET
>(UNSET);
const $touchModalItemOrderByGroupOverride = atom<
  Record<string, string[]> | typeof UNSET
>(UNSET);

export const $touchModalColorChanges = atom<
  Record<string, TouchModalColorChange>
>({});

export const $touchModalOpen = atom(false);

export const $touchModalEditMode = atom(
  (get) => get($touchModalEditModeInternal),
  (_get, set, newValue: boolean) => {
    if (!newValue) {
      set($touchModalGroupIdsOverride, UNSET);
      set($touchModalGroupOrderIdsOverride, UNSET);
      set($touchModalItemFavoritesByGroupOverride, UNSET);
      set($touchModalItemOrderByGroupOverride, UNSET);
      set($touchModalColorChanges, {});
    }
    set($touchModalEditModeInternal, newValue);
  },
);

// 🟢🟢🟢 COMPUTED DATA -> Touch modal group IDs (edit mode override or server value)
export const $touchModalGroupIds = atom(
  (get) => {
    const override = get($touchModalGroupIdsOverride);
    if (override !== UNSET) return override;
    return get($scaleTicketQuickAddGroupIds);
  },
  (_get, set, newValue: string[]) => {
    set($touchModalGroupIdsOverride, newValue);
  },
);

export const $touchModalGroupOrderIds = atom(
  (get) => {
    const override = get($touchModalGroupOrderIdsOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return [] as string[];

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.touchModalGroupOrderIds,
    );
    if (Array.isArray(value)) return value as string[];
    return [] as string[];
  },
  (_get, set, newValue: string[]) => {
    set($touchModalGroupOrderIdsOverride, newValue);
  },
);

// 🟢🟢🟢 COMPUTED DATA -> Per-group item favorites
export const $touchModalItemFavoritesByGroup = atom(
  (get) => {
    const override = get($touchModalItemFavoritesByGroupOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return {} as Record<string, string[]>;

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.quickAddItemIds,
    );
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, string[]>;
    }
    return {} as Record<string, string[]>;
  },
  (_get, set, newValue: Record<string, string[]>) => {
    set($touchModalItemFavoritesByGroupOverride, newValue);
  },
);

export const $touchModalItemOrderByGroup = atom(
  (get) => {
    const override = get($touchModalItemOrderByGroupOverride);
    if (override !== UNSET) return override;

    const { data } = get($allSettingsGroups);
    if (!data?.settingsGroups) return {} as Record<string, string[]>;

    const value = findSettingUserValue(
      data.settingsGroups,
      SETTING_IDS.touchModalItemOrderByGroup,
    );
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, string[]>;
    }
    return {} as Record<string, string[]>;
  },
  (_get, set, newValue: Record<string, string[]>) => {
    set($touchModalItemOrderByGroupOverride, newValue);
  },
);

// 🟢🟢🟢 COMPUTED DATA -> Has unsaved changes
export const $touchModalHasChanges = atom((get) => {
  const groupOverride = get($touchModalGroupIdsOverride);
  const groupOrderOverride = get($touchModalGroupOrderIdsOverride);
  const itemFavOverride = get($touchModalItemFavoritesByGroupOverride);
  const itemOrderOverride = get($touchModalItemOrderByGroupOverride);
  const colorChanges = get($touchModalColorChanges);

  if (groupOverride !== UNSET) return true;
  if (groupOrderOverride !== UNSET) return true;
  if (itemFavOverride !== UNSET) return true;
  if (itemOrderOverride !== UNSET) return true;
  if (Object.keys(colorChanges).length > 0) return true;
  return false;
});

// 🟠🟠🟠 ACTION -> Save touch modal mutations
const $updateGroupColor = itemGroupUpdateAtom({});
const $updateItemColor = itemUpdateAtom({});

export const $saveTouchModalEdits = atom(
  null,
  async (get, set, params: { errorMessage: string }) => {
    const groupOverride = get($touchModalGroupIdsOverride);
    const groupOrderOverride = get($touchModalGroupOrderIdsOverride);
    const itemFavOverride = get($touchModalItemFavoritesByGroupOverride);
    const itemOrderOverride = get($touchModalItemOrderByGroupOverride);
    const colorChanges = get($touchModalColorChanges);
    const user = get($user);

    try {
      const settingsValues: Record<string, unknown> = {};
      if (groupOverride !== UNSET) {
        settingsValues[SETTING_IDS.quickAddGroupIds] = groupOverride;
      }
      if (groupOrderOverride !== UNSET) {
        settingsValues[SETTING_IDS.touchModalGroupOrderIds] =
          groupOrderOverride;
      }
      if (itemFavOverride !== UNSET) {
        settingsValues[SETTING_IDS.quickAddItemIds] = itemFavOverride;
      }
      if (itemOrderOverride !== UNSET) {
        settingsValues[SETTING_IDS.touchModalItemOrderByGroup] =
          itemOrderOverride;
      }

      if (Object.keys(settingsValues).length > 0 && user) {
        await set($patchScaleTicketSetting, {
          scope: 'User' as const,
          objectId: user.id,
          values: settingsValues,
        });
      }

      const entries = Object.entries(colorChanges);
      await Promise.all(
        entries.map(([entityId, change]) =>
          change.type === 'group'
            ? set($updateGroupColor, {
                id: entityId,
                colorHue: change.colorHue,
              })
            : set($updateItemColor, {
                id: entityId,
                colorHue: change.colorHue,
              }),
        ),
      );

      await get($allSettingsGroups).refetch();
      await get($inventoryItems).refetch();

      set($touchModalEditModeInternal, false);
      set($touchModalGroupIdsOverride, UNSET);
      set($touchModalGroupOrderIdsOverride, UNSET);
      set($touchModalItemFavoritesByGroupOverride, UNSET);
      set($touchModalItemOrderByGroupOverride, UNSET);
      set($touchModalColorChanges, {});
      set($touchModalOpen, false);
    } catch {
      toastQueue.add(
        { title: params.errorMessage, variant: 'error' },
        { timeout: 5000 },
      );
    }
  },
);
